import Layout from "@/components/layout/Layout";
import { motion } from "framer-motion";
import { Clock, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { posts } from "@/data/blogPosts";

const Blog = () => {
  return (
    <Layout>
      <Helmet>
        <title>Blog — Cloudease</title>
        <meta name="description" content="Practical guides, industry insights, and best practices from the Cloudease engineering team." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">Insights</p>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold tracking-tight mb-6">
              Field notes from <span className="text-gradient">production cloud.</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Practical guides, incident post-mortems, and hard-won lessons from our engineering team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 lg:px-8">

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block h-full bg-card border border-border rounded-xl overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-48 bg-gradient-to-br from-cloud-100 to-cloud-200 flex items-center justify-center">
                    <span className="px-3 py-1 text-xs font-semibold bg-primary/10 text-primary rounded-full">{post.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                      <div className="flex items-center gap-1"><User className="h-3 w-3" /> {post.author}</div>
                      <div className="flex items-center gap-3">
                        <span>{post.date}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                      Read article <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
